import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Package, ArchiveRestore, Users2, Wrench, Ban, AlertCircle, PackagePlus, UserPlus,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { HistorialEquipoDialog } from '@/components/inventario/HistorialEquipoDialog';
import { AltaEquipoDialog } from '@/components/inventario/AltaEquipoDialog';
import { AsignarEquipoDialog } from '@/components/inventario/AsignarEquipoDialog';
import { DevolverEquipoDialog, type DevolverableItem } from '@/components/inventario/DevolverEquipoDialog';
import { EditarCondicionDialog, type EditarCondicionItem } from '@/components/inventario/EditarCondicionDialog';
import { EquiparEmpleadoDialog } from '@/components/inventario/EquiparEmpleadoDialog';
import { DarDeBajaDialog, type DarDeBajaItem } from '@/components/inventario/DarDeBajaDialog';
import { InventarioLista } from '@/components/inventario/InventarioLista';
import { getInventarioDashboard, type InventarioItem } from '@/services/api/inventario.api';
import { formatNumber, cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { ReportButton } from '@/components/reportes/ReportButton';

const STATS = [
  { key: 'total_activos',   label: 'Total',       icon: Package,       color: 'border-primary-200 bg-primary-50/70 text-primary-700' },
  { key: 'total_asignados', label: 'Asignados',   icon: Users2,        color: 'border-emerald-200 bg-emerald-50/70 text-emerald-700' },
  { key: 'total_bodega',    label: 'En bodega',   icon: ArchiveRestore, color: 'border-cyan-200 bg-cyan-50/70 text-cyan-700' },
  { key: 'total_reparacion',label: 'Reparación',  icon: Wrench,        color: 'border-amber-200 bg-amber-50/70 text-amber-700' },
  { key: 'total_baja',      label: 'De baja',     icon: Ban,           color: 'border-slate-200 bg-slate-50/70 text-slate-600' },
] as const;

export function InventarioPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['inventario-dashboard'],
    queryFn: () => getInventarioDashboard(),
    refetchInterval: 60_000,
  });

  const puedeCrear = useAuthStore((s) => s.hasRole(['ADMIN', 'COMPRAS']));
  const esCompras = useAuthStore((s) => s.user?.rol === 'COMPRAS');
  const [altaOpen, setAltaOpen] = useState(false);
  const [historialId, setHistorialId] = useState<string | null>(null);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [asignarItem, setAsignarItem] = useState<InventarioItem | null>(null);
  const [devolverItem, setDevolverItem] = useState<DevolverableItem | null>(null);
  const [editarCondicionItem, setEditarCondicionItem] = useState<EditarCondicionItem | null>(null);
  const [equiparOpen, setEquiparOpen] = useState(false);
  const [darDeBajaItem, setDarDeBajaItem] = useState<DarDeBajaItem | null>(null);

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Inventario</h1>
          <p className="text-sm text-muted-foreground">
            {esCompras ? 'Inventario de Compras.' : 'Catálogo unificado de activos GLPI y Compras.'}
          </p>
        </div>
        <div className="flex gap-2">
          <ReportButton reporte="inventario-activos" />
          <Button variant="outline" onClick={() => setEquiparOpen(true)} className="shrink-0">
            <UserPlus className="h-4 w-4" />
            Equipar colaborador
          </Button>
          {puedeCrear && (
            <Button onClick={() => setAltaOpen(true)} className="shrink-0">
              <PackagePlus className="h-4 w-4" />
              Registrar
            </Button>
          )}
        </div>
      </div>

      {/* ── Stats strip ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {STATS.map(({ key, label, icon: Icon, color }) => (
          <div
            key={key}
            className={cn(
              'flex items-center gap-2.5 rounded-xl border px-4 py-2.5 min-w-[108px] transition-opacity',
              isLoading && 'opacity-50 animate-pulse',
              color,
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <div className="leading-none">
              <p className="text-lg font-bold tabular-nums">
                {isLoading ? '—' : formatNumber((data as Record<string, number> | undefined)?.[key] ?? 0)}
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-wide mt-0.5 opacity-70">
                {label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Low-stock alert ─────────────────────────────────────────── */}
      {data?.stock_bajo && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50/70 px-4 py-3">
          <AlertCircle className="h-5 w-5 text-amber-700 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-900">
              Inventario en bodega bajo el umbral mínimo
            </p>
            <p className="text-xs text-amber-800">
              Quedan {formatNumber(data.total_bodega)} activos disponibles. Considera reabastecer.
            </p>
          </div>
        </div>
      )}

      {/* ── Inventory list ──────────────────────────────────────────── */}
      <InventarioLista
        onVerHistorial={(id) => {
          setHistorialId(id);
          setHistorialOpen(true);
        }}
        onAsignar={(item) => setAsignarItem(item)}
        onDevolver={(item) => setDevolverItem(item)}
        onEditarCondicion={(item) => setEditarCondicionItem(item)}
        onDarDeBaja={(item) => setDarDeBajaItem(item)}
      />

      {/* ── Dialogs ─────────────────────────────────────────────────── */}
      <HistorialEquipoDialog
        idItemMaestro={historialId}
        open={historialOpen}
        onOpenChange={setHistorialOpen}
      />
      <AltaEquipoDialog open={altaOpen} onOpenChange={setAltaOpen} />

      <AsignarEquipoDialog
        item={asignarItem}
        open={asignarItem !== null}
        onOpenChange={(open) => { if (!open) setAsignarItem(null); }}
      />

      <DevolverEquipoDialog
        item={devolverItem}
        open={devolverItem !== null}
        onOpenChange={(open) => { if (!open) setDevolverItem(null); }}
      />

      <EditarCondicionDialog
        key={editarCondicionItem?.id_item_maestro ?? 'none'}
        item={editarCondicionItem}
        onOpenChange={(open) => { if (!open) setEditarCondicionItem(null); }}
      />

      <EquiparEmpleadoDialog
        open={equiparOpen}
        onOpenChange={setEquiparOpen}
      />

      <DarDeBajaDialog
        key={darDeBajaItem?.id_item_maestro ?? 'none'}
        item={darDeBajaItem}
        onOpenChange={(open) => { if (!open) setDarDeBajaItem(null); }}
      />
    </div>
  );
}
